import { useDrawer } from '@src/components/Drawer';
import { useCallbackRef } from '@src/hooks/useCallbackRef';
import { AppwideDrawerNavigationBehavior } from '@src/providers/AppwideDrawerProvider/AppwideDrawerNavigationBehavior';
import { useEffect, useState, type PropsWithChildren, type ReactNode } from 'react';
import type { AppwideDrawerContentType } from './AppwideDrawerContentType';
import { AppwideDrawerContext } from './useAppwideDrawerContext';

export interface AppwideDrawerContentSpec {
  type: AppwideDrawerContentType;
  title?: string;
  /** when drawerHeaderDesign is 'flex' and this is provided, this will be used to render the header content */
  flexHeaderTitle?: () => ReactNode;
  renderContent: () => ReactNode;
  renderHeaderContent?: () => ReactNode;
  /** Width of drawer, defaults to 450 */
  width?: number;
  /** CSS Position of drawer, defaults to relative. Determines if drawer is opened next to content ('relative') or on top of content ('absolute') */
  position?: 'relative' | 'absolute';
  placement?: 'right' | 'left';
  /** The type of header to use: 'tab' for tab-based header, 'flex' for simple flex header, defaults to 'tab' */
  drawerHeaderDesign?: 'tab' | 'flex';
}

export const AppwideDrawerProvider = function AppwideDrawerProvider({ children }: PropsWithChildren) {
  const [contentSpec, setContentSpec] = useState<AppwideDrawerContentSpec | undefined>(undefined);
  const [isGuideVisible, setIsGuideVisible] = useState(false);

  const drawer = useDrawer({
    closeOnEscape: true,
    width: contentSpec?.width ?? 450,
    position: contentSpec?.position ?? 'relative',
    placement: contentSpec?.placement,
  });

  const openDrawer = useCallbackRef((spec: AppwideDrawerContentSpec) => {
    setContentSpec(spec);
    drawer.open();
  });

  const toggleGuide = useCallbackRef(() => setIsGuideVisible(v => !v));
  const resetGuide = useCallbackRef(() => setIsGuideVisible(false));

  // Reset guide visibility whenever the drawer closes
  useEffect(() => {
    if (!drawer.isOpen) {
      setIsGuideVisible(false);
    }
  }, [drawer.isOpen]);

  return (
    <AppwideDrawerContext.Provider
      value={{
        drawer,
        openDrawer,
        closeDrawer: drawer.close,
        contentSpec,
        isOpen: drawer.isOpen,
        isGuideVisible,
        toggleGuide,
        resetGuide,
      }}
    >
      <AppwideDrawerNavigationBehavior drawer={drawer} contentSpec={contentSpec} />

      {children}
    </AppwideDrawerContext.Provider>
  );
};
